<script setup lang="ts">
import { logPageView, roomsPath } from '../../configs/firebase';
import { useRealtimeDatabaseList } from '../../composables/useRealtimeDatabaseList';
import { Room } from '../../interfaces/Room.interface';
import { useRoute } from 'vue-router';
import {
  HERO_BG_IMAGE,
  LANGUAGE_EMOJIS,
  LANGUAGE_IMAGES,
  LANGUAGE_KEYS,
  DIFFICULTY_KEYS,
  difficultyColors,
} from '../../utils/constants';
import { computed, onMounted, ref, watch } from 'vue';
import {
  DifficultyLevel,
  Language,
  TagKey,
  useTranslations,
} from '../../composables/useTranslations';
import CreateRoomModal from '../../components/CreateRoomModal.vue';
import {
  Plus,
  Search,
  Users,
  XCircle,
  ChevronDown,
  ChevronUp,
} from 'lucide-vue-next';
import { getTimeAgo } from '../../utils/time';
import FeedbackWidget from '../../components/FeedbackWidget.vue';
import { getLocalizedPath } from '../../composables/useLanguage';

// 1. State declarations
const {
  items: rooms,
  isLoading,
  error,
} = useRealtimeDatabaseList<Room[]>(roomsPath);
const route = useRoute();
const t = useTranslations();

const filteredRooms = ref<Room[]>([]);

// Get language from URL and normalize it
const urlLanguage = route.query['language'] as Language;
const normalizedLanguage =
  LANGUAGE_KEYS.find(
    (lang) => lang.toLowerCase() === urlLanguage?.toLowerCase(),
  ) || 'all';

const isModalOpen = ref(false);
const isFilterOpen = ref(true);

const searchQuery = ref('');
const selectedDifficulty = ref<DifficultyLevel | 'all'>('all');
const selectedLanguage = ref<Language | 'all'>(normalizedLanguage);

// 2. Function declarations
const handleFilterChange = () => {
  if (!rooms.value) {
    return;
  }
  const filtered = rooms.value.filter((room) => {
    const matchesSearch =
      searchQuery.value.toLowerCase() === '' ||
      room.title.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
      room.description
        .toLowerCase()
        .includes(searchQuery.value.toLowerCase()) ||
      room.language.toLowerCase().includes(searchQuery.value.toLowerCase());
    const matchesDifficulty =
      selectedDifficulty.value === 'all' ||
      room.difficulty === selectedDifficulty.value;
    const matchesLanguage =
      selectedLanguage.value === 'all' ||
      room.language.toLowerCase() === selectedLanguage.value.toLowerCase();
    return matchesSearch && matchesDifficulty && matchesLanguage;
  });
  filteredRooms.value = filtered;
};

function formatTimeAgo(dateString: string): string {
  const { value, unit } = getTimeAgo(dateString);

  if (unit === 'just_now') return t.value.justNow;

  if (value === 1) {
    // For singular: minuteAgo, hourAgo, dayAgo, etc.
    return t.value[`${unit}Ago`];
  } else {
    // For plural: minutesAgo, hoursAgo, daysAgo, etc.
    return t.value[`${unit}sAgo`].replace('{0}', value.toString());
  }
}

// 3. Computed properties
const currentLanguageImage = computed(() =>
  selectedLanguage.value !== 'all'
    ? LANGUAGE_IMAGES[selectedLanguage.value]
    : HERO_BG_IMAGE,
);

const backgroundPositionClass = computed(() =>
  selectedLanguage.value === 'all'
    ? 'sm:bg-[center_top_-10rem] md:bg-[center_top_-15rem] xl:bg-[center_top_-25rem] 2xl:bg-[center_top_-35rem]'
    : 'bg-center',
);

const hasActiveFilters = computed(() => {
  return (
    searchQuery.value !== '' ||
    selectedDifficulty.value !== 'all' ||
    selectedLanguage.value !== 'all'
  );
});

// 4. Watchers
watch([searchQuery, selectedDifficulty, selectedLanguage, rooms], () => {
  if (!rooms.value) {
    return;
  }
  if (rooms.value.length > 0) {
    handleFilterChange();
  }
});

// 5. Lifecycle hooks
onMounted(() => {
  logPageView('Rooms');
  if (!rooms.value) {
    return;
  }

  if (rooms.value.length > 0) {
    handleFilterChange();
  }
});
</script>

<template>
  <section class="h-full">
    <div class="bg-linear-to-br from-gray-50 via-white to-gray-100 h-full">
      <!-- Hero Section -->
      <div
        :class="[
          'relative h-80 bg-cover transition-all duration-500',
          backgroundPositionClass,
        ]"
        :style="{
          backgroundImage: `url('${currentLanguageImage}')`,
        }"
      >
        <div class="absolute inset-0 bg-linear-to-b from-black/50 to-black/70">
          <div
            class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex flex-col justify-center relative"
          >
            <div class="flex justify-between items-center">
              <div>
                <h1
                  class="text-5xl font-bold text-white mb-4 flex items-center gap-4"
                >
                  <span v-if="selectedLanguage !== 'all'" class="text-4xl">
                    {{ LANGUAGE_EMOJIS[selectedLanguage] }}
                  </span>
                  {{ t.rooms }}
                </h1>
                <p class="text-xl text-gray-200 max-w-2xl">
                  {{ t.findThePerfectRoomForYou }}
                </p>
              </div>
              <button
                class="inline-flex items-center px-6 py-3 rounded-lg bg-white hover:bg-gray-50 transition-colors shadow-lg"
                @click="isModalOpen = true"
              >
                <Plus class="w-5 h-5 mr-2" />
                {{ t.createRoom }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Main Content -->
      <div
        class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 -mt-16 relative z-10"
      >
        <div
          class="bg-white rounded-2xl shadow-lg mb-8 select-none"
          :class="{
            'p-6': isFilterOpen,
            'p-0 md:p-6': !isFilterOpen,
          }"
        >
          <!-- Filter Header with Toggle - Entire row is clickable -->
          <div
            class="flex items-center justify-between cursor-pointer md:cursor-auto md:pointer-events-none"
            :class="{ 'p-6 md:p-0': !isFilterOpen }"
            @click="isFilterOpen = !isFilterOpen"
          >
            <h2 class="text-xl font-semibold flex items-center">
              <svg
                class="w-5 h-5 mr-2 text-secondary"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path
                  d="M3 4a1 1 0 0 1 1-1h16a1 1 0 0 1 1 1v2.586a1 1 0 0 1-.293.707l-6.414 6.414a1 1 0 0 0-.293.707V17l-4 4v-6.586a1 1 0 0 0-.293-.707L3.293 7.293A1 1 0 0 1 3 6.586V4z"
                />
              </svg>
              {{ t.filterRooms }}
            </h2>

            <!-- Mobile Toggle Button -->
            <div class="md:hidden flex items-center justify-center">
              <ChevronUp v-if="isFilterOpen" class="h-5 w-5 text-gray-500" />
              <ChevronDown v-else class="h-5 w-5 text-gray-500" />
            </div>
          </div>

          <!-- Filter Content - Collapsible on Mobile -->
          <div
            class="transition-all duration-300 overflow-hidden mt-4"
            :class="{ 'hidden md:block': !isFilterOpen }"
          >
            <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
              <!-- Search Bar -->
              <div class="relative">
                <div
                  class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
                >
                  <Search class="h-5 w-5 text-secondary" />
                </div>
                <input
                  v-model="searchQuery"
                  type="text"
                  :placeholder="t.searchRooms"
                  class="block w-full pl-10 pr-4 py-2 border-0 ring-1 ring-gray-200 rounded-lg focus:ring-2 bg-gray-50"
                />
              </div>

              <!-- Language Filter -->
              <div class="relative">
                <select
                  v-model="selectedLanguage"
                  class="block w-full pl-3 pr-10 py-2 text-base border-0 ring-1 ring-gray-200 rounded-lg focus:ring-2 bg-gray-50"
                >
                  <option value="all">🌐 {{ t.allLanguages }}</option>
                  <option
                    v-for="lang in LANGUAGE_KEYS"
                    :key="lang"
                    :value="lang"
                  >
                    {{ LANGUAGE_EMOJIS[lang] }} {{ t[lang] }}
                  </option>
                </select>
              </div>

              <!-- Difficulty Filter -->
              <div class="relative">
                <select
                  v-model="selectedDifficulty"
                  class="block w-full pl-3 pr-10 py-2 text-base border-0 ring-1 ring-gray-200 rounded-lg focus:ring-2 bg-gray-50"
                >
                  <option value="all">{{ t.allLevels }}</option>
                  <option
                    v-for="level in DIFFICULTY_KEYS"
                    :key="level"
                    :value="level"
                  >
                    {{ t[level] }}
                  </option>
                </select>
              </div>
            </div>

            <!-- Active Filters -->
            <div
              v-if="hasActiveFilters"
              class="flex flex-wrap items-center gap-2 mt-3 text-sm"
            >
              <span class="text-secondary">{{ t.activeFilters }}:</span>
              <div class="flex flex-wrap gap-2">
                <div
                  v-if="searchQuery"
                  class="inline-flex items-center px-2 py-1 rounded-full text-sm bg-gray-100"
                >
                  {{ t.search }}: "{{ searchQuery }}"
                  <XCircle
                    class="h-4 w-4 ml-1 cursor-pointer"
                    @click="searchQuery = ''"
                  />
                </div>

                <div
                  v-if="selectedLanguage !== 'all'"
                  class="inline-flex items-center px-2 py-1 rounded-full text-sm"
                >
                  {{ t[selectedLanguage] }}
                  <XCircle
                    class="h-4 w-4 ml-1 cursor-pointer"
                    @click="selectedLanguage = 'all'"
                  />
                </div>

                <div
                  v-if="selectedDifficulty !== 'all'"
                  :class="[
                    'inline-flex items-center px-2 py-1 rounded-full text-sm',
                    difficultyColors[selectedDifficulty].bg,
                  ]"
                >
                  {{ t[selectedDifficulty] }}
                  <XCircle
                    class="h-4 w-4 ml-1 cursor-pointer"
                    @click="selectedDifficulty = 'all'"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Loading State -->
        <div v-if="isLoading" class="flex justify-center items-center py-12">
          <div
            class="animate-spin rounded-full h-12 w-12 border-b-2 border-accent"
          ></div>
        </div>

        <!-- Error State -->
        <div
          v-else-if="error"
          class="bg-red-50 border border-red-200 rounded-2xl p-6 text-center"
        >
          <div class="max-w-md mx-auto">
            <div
              class="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="32"
                height="32"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#ef4444"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                ></path>
              </svg>
            </div>
            <h3 class="text-xl font-semibold text-gray-900 mb-2">
              {{ t.error }}
            </h3>
            <p class="text-gray-600 mb-6">{{ error.message }}</p>
          </div>
        </div>

        <!-- Rooms Grid -->
        <div
          v-else-if="filteredRooms.length > 0"
          class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          <div v-for="room in filteredRooms" :key="room.id">
            <div
              class="group bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100 h-full flex flex-col"
            >
              <!-- Top Banner with Language & Difficulty -->
              <div
                class="h-20 rounded-t-2xl bg-gradient-to-r from-primary/5 to-accent/5 p-5 flex items-center justify-between"
              >
                <div class="flex items-center gap-3">
                  <div
                    class="w-12 h-12 rounded-xl bg-white shadow-sm flex items-center justify-center"
                  >
                    <span class="text-2xl"
                      >{{ LANGUAGE_EMOJIS[room.language] }}
                    </span>
                  </div>
                  <span class="font-medium">{{
                    t[room.language as Language]
                  }}</span>
                </div>

                <span
                  :class="[
                    'px-4 py-1.5 rounded-full text-sm font-medium',
                    difficultyColors[room.difficulty].bg,
                    difficultyColors[room.difficulty].text,
                  ]"
                >
                  {{ t[room.difficulty] }}
                </span>
              </div>

              <!-- Content -->
              <div class="p-5 space-y-4 flex-grow flex flex-col">
                <div class="flex-grow">
                  <h3
                    class="text-xl font-bold line-clamp-1 group-hover:text-accent transition-colors"
                  >
                    {{ room.title }}
                  </h3>
                  <p class="text-gray-600 mt-2 line-clamp-2">
                    {{ room.description }}
                  </p>
                </div>

                <!-- Tags -->
                <div class="flex flex-wrap gap-2">
                  <span
                    v-for="tag in room.tags"
                    :key="tag"
                    class="px-3 py-1 bg-gray-50 rounded-full text-sm text-gray-600 hover:bg-gray-100 transition-colors"
                  >
                    {{ t[tag as TagKey] }}
                  </span>
                </div>

                <!-- Footer -->
                <div
                  class="flex items-center justify-between pt-4 border-t border-gray-100"
                >
                  <div class="flex items-center gap-2 text-sm text-gray-500">
                    <div class="flex items-center gap-1">
                      <Users class="w-4 h-4" />
                      <span
                        >{{ Object.keys(room.participants || {}).length }} /
                        5</span
                      >
                    </div>
                    <span class="text-gray-300">•</span>
                    <span>{{ formatTimeAgo(room.createdAt) }}</span>
                  </div>

                  <router-link
                    :to="getLocalizedPath(`/rooms/${room.id}`)"
                    class="px-5 py-2 bg-accent text-white rounded-xl hover:bg-accent/90 active:scale-95 transition-all"
                    :class="{
                      'opacity-50 cursor-not-allowed pointer-events-none':
                        Object.keys(room.participants || {}).length >= 5,
                    }"
                  >
                    {{ t.join }}
                  </router-link>
                </div>
              </div>
            </div>
          </div>

          <!-- CTA: Create your own room -->
          <div
            class="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 text-center flex flex-col items-center justify-center"
          >
            <div
              class="w-16 h-16 bg-[#dd7631]/10 rounded-full flex items-center justify-center mx-auto mb-4"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="32"
                height="32"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#dd7631"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M12 5v14M5 12h14"></path>
              </svg>
            </div>
            <h3 class="text-xl font-semibold text-gray-900 mb-2">
              {{ t.didntFindARoomThatFitsYou }}
            </h3>
            <p class="text-gray-600 mb-6">
              {{ t.createYourOwnRoomAndStartAConversation }}
            </p>
            <button
              class="inline-flex items-center px-6 py-3 rounded-lg bg-[#dd7631] text-white hover:bg-[#c56a2c] transition-colors shadow-lg"
              @click="isModalOpen = true"
            >
              <Plus class="w-5 h-5 mr-2" />
              {{ t.createARoom }}
            </button>
          </div>
        </div>

        <div v-else>
          <div
            class="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 text-center"
          >
            <div class="max-w-md mx-auto">
              <div
                class="w-16 h-16 bg-[#dd7631]/10 rounded-full flex items-center justify-center mx-auto mb-4"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#dd7631"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M12 5v14M5 12h14"></path>
                </svg>
              </div>
              <h3 class="text-xl font-semibold text-gray-900 mb-2">
                {{ t.noRoomsFound }}
              </h3>
              <p class="text-gray-600 mb-6">
                {{ t.itLooksLikeThereAreNoRooms }}
              </p>
              <button
                class="inline-flex items-center px-6 py-3 rounded-lg bg-[#dd7631] text-white hover:bg-[#c56a2c] transition-colors shadow-lg"
                @click="isModalOpen = true"
              >
                <Plus class="w-5 h-5 mr-2" />
                {{ t.createRoom }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <CreateRoomModal
        v-if="isModalOpen"
        v-model="isModalOpen"
        :default-language="
          selectedLanguage !== 'all' ? selectedLanguage : 'English'
        "
        :default-difficulty="
          selectedDifficulty !== 'all' ? selectedDifficulty : 'Beginner'
        "
      />
    </div>
  </section>

  <RouterView />
  <FeedbackWidget type="fab" />
</template>
